// src/services/pipsCreatureKeeperService.ts
import { logger } from '../utils/logger';
// FIXED: Added field journal entry when selecting starter creature

import { supabase } from './supabase';
import { Game, GameData, Creature, PlayerCreature, FieldJournalEntry } from '../types/pipsCreatureKeeper';

/**
 * Create a new game for a user
 */
export async function createGame(userId: string, playerName: string): Promise<Game> {
  const { data, error } = await supabase
    .from('creature_catcher_games')
    .insert({
      user_id: userId,
      player_name: playerName
    })
    .select()
    .single();

  if (error) throw error;

  // Unlock first territory and habitat
  await Promise.all([
    unlockTerritory(data.id, 1),
    unlockHabitat(data.id, 1)
  ]);

  return data;
}

/**
 * Load a user's existing game
 */
export async function loadGame(userId: string): Promise<Game | null> {
  const { data, error } = await supabase
    .from('creature_catcher_games')
    .select('*')
    .eq('user_id', userId)
    .single();

  if (error) {
    if (error.code === 'PGRST116') return null;
    throw error;
  }

  return data;
}

/**
 * Load all game data at once (optimized with single queries)
 */
export async function loadGameData(gameId: string): Promise<GameData> {
  const [
    creatures,
    territories,
    habitats,
    myCreatures,
    fieldJournal,
    typeCompatibility,
    creatureTerritories,
    unlockedHabitatsRaw,
    unlockedTerritoriesRaw
  ] = await Promise.all([
    loadCreatures(),
    loadTerritories(),
    loadHabitats(),
    loadPlayerCreatures(gameId),
    loadFieldJournal(gameId),
    loadTypeCompatibility(),
    loadCreatureTerritories(),
    loadUnlockedHabitats(gameId),
    loadUnlockedTerritories(gameId)
  ]);

  // CRITICAL FIX: Convert single max ID to array of all IDs
  const maxHabitatId = Array.isArray(unlockedHabitatsRaw) && unlockedHabitatsRaw.length > 0
    ? Math.max(...unlockedHabitatsRaw)
    : (unlockedHabitatsRaw[0] || 0);
  const unlockedHabitats = maxHabitatId > 0
    ? Array.from({ length: maxHabitatId }, (_, i) => i + 1)
    : [];

  const maxTerritoryId = Array.isArray(unlockedTerritoriesRaw) && unlockedTerritoriesRaw.length > 0
    ? Math.max(...unlockedTerritoriesRaw)
    : (unlockedTerritoriesRaw[0] || 0);
  const unlockedTerritories = maxTerritoryId > 0
    ? Array.from({ length: maxTerritoryId }, (_, i) => i + 1)
    : [];

  logger.log('Loaded game data:', {
    habitats: habitats.length,
    maxHabitatId,
    unlockedHabitats,
    myCreatures: myCreatures.length,
    fieldJournal: fieldJournal.length
  });

  // Find the current exploring companion
  const exploringCreature = myCreatures.find(pc => pc.is_exploring);
  const selectedCompanion = exploringCreature
    ? creatures.find(c => c.id === exploringCreature.creature_id) || null
    : null;

  return {
    creatures,
    territories,
    habitats,
    myCreatures,
    fieldJournal,
    typeCompatibility,
    creatureTerritories,
    selectedCompanion,
    habitatPlacements: {},
    unlockedHabitats,
    unlockedTerritories,
    selectedHabitatIndex: 0
  };
}

/**
 * Load all creatures
 */
async function loadCreatures(): Promise<Creature[]> {
  const { data, error } = await supabase
    .from('creature_catcher_creatures')
    .select('*')
    .order('id');

  if (error) throw error;
  return data || [];
}

/**
 * Load all territories
 */
async function loadTerritories() {
  const { data, error } = await supabase
    .from('creature_catcher_territories')
    .select('*')
    .order('order_index');

  if (error) throw error;
  return data || [];
}

/**
 * Load all habitats
 */
async function loadHabitats() {
  const { data, error } = await supabase
    .from('creature_catcher_habitats')
    .select('*')
    .order('id');

  if (error) throw error;
  return data || [];
}

/**
 * Load player's creatures
 */
async function loadPlayerCreatures(gameId: string): Promise<PlayerCreature[]> {
  const { data, error } = await supabase
    .from('creature_catcher_my_creatures')
    .select('*, creature_catcher_creatures(*)')
    .eq('game_id', gameId)
    .order('captured_at');

  if (error) throw error;
  return data || [];
}

/**
 * Load field journal
 */
async function loadFieldJournal(gameId: string): Promise<FieldJournalEntry[]> {
  const { data, error } = await supabase
    .from('creature_catcher_field_journal')
    .select('*, creature_catcher_creatures(*)')
    .eq('game_id', gameId)
    .order('discovered_at');

  if (error) throw error;
  return data || [];
}

/**
 * Load type compatibility
 */
async function loadTypeCompatibility() {
  const { data, error } = await supabase
    .from('creature_catcher_type_compatibility')
    .select('*');

  if (error) throw error;
  return data || [];
}

/**
 * Load creature territories
 */
async function loadCreatureTerritories() {
  const { data, error } = await supabase
    .from('creature_catcher_creature_territories')
    .select('*');

  if (error) throw error;
  return data || [];
}

/**
 * Load unlocked habitats
 */
async function loadUnlockedHabitats(gameId: string): Promise<number[]> {
  const { data, error } = await supabase
    .from('creature_catcher_games')
    .select('unlocked_habitats')
    .eq('id', gameId)
    .single();

  if (error) throw error;
  return data ? [data.unlocked_habitats] : [];
}

/**
 * Load unlocked territories
 */
async function loadUnlockedTerritories(gameId: string): Promise<number[]> {
  const { data, error } = await supabase
    .from('creature_catcher_games')
    .select('unlocked_territories')
    .eq('id', gameId)
    .single();

  if (error) throw error;
  return data ? [data.unlocked_territories] : [];
}

/**
 * Add starter creature to player's collection
 * FIXED: Now also adds to field journal
 */
export async function addStarterCreature(gameId: string, creatureId: number): Promise<void> {
  logger.log('Adding starter creature:', { gameId, creatureId });

  // 1. Capture the starter creature
  const { error: captureError } = await supabase
    .from('creature_catcher_my_creatures')
    .insert({
      game_id: gameId,
      creature_id: creatureId,
      habitat_id: 1,
      habitat_slot: 0,
      current_happiness: 70,
      is_exploring: true
    });

  if (captureError) {
    logger.error('Error capturing starter:', captureError);
    throw captureError;
  }

  logger.log('Starter captured successfully');

  // 2. Add to field journal
  const { error: journalError } = await supabase
    .from('creature_catcher_field_journal')
    .insert({
      game_id: gameId,
      creature_id: creatureId
    });

  if (journalError) {
    // Ignore duplicate errors (code 23505)
    if (journalError.code !== '23505') {
      logger.error('Error adding starter to field journal:', journalError);
      throw journalError;
    }
  }

  logger.log('Starter added to field journal successfully');
}

/**
 * Capture a creature
 */
export async function captureCreature(gameId: string, creatureId: number): Promise<void> {
  logger.log('Capturing creature:', creatureId, '- will be UNPLACED');

  const { error } = await supabase
    .from('creature_catcher_my_creatures')
    .insert({
      game_id: gameId,
      creature_id: creatureId,
      habitat_id: null,
      habitat_slot: null,
      current_happiness: 70,
      is_exploring: false
    });

  if (error) throw error;
  logger.log('Creature captured as UNPLACED');
}

/**
 * Add creature to field journal
 */
export async function addToFieldJournal(gameId: string, creatureId: number): Promise<void> {
  const { data: existing, error: checkError } = await supabase
    .from('creature_catcher_field_journal')
    .select('id')
    .eq('game_id', gameId)
    .eq('creature_id', creatureId)
    .maybeSingle();

  if (checkError) throw checkError;

  if (existing) {
    logger.log('Creature already in journal, skipping');
    return;
  }

  logger.log('Adding new creature to field journal');
  const { error } = await supabase
    .from('creature_catcher_field_journal')
    .insert({
      game_id: gameId,
      creature_id: creatureId
    });

  if (error) throw error;
}

/**
 * Update creature placement in habitat
 */
export async function updateCreaturePlacement(
  playerCreatureId: string,
  habitatId: number | null,
  slot: number | null
): Promise<void> {
  const { error } = await supabase
    .from('creature_catcher_my_creatures')
    .update({
      habitat_id: habitatId,
      habitat_slot: slot
    })
    .eq('id', playerCreatureId);

  if (error) throw error;
}

/**
 * Update creature happiness
 */
export async function updateCreatureHappiness(
  playerCreatureId: string,
  happiness: number
): Promise<void> {
  const { error } = await supabase
    .from('creature_catcher_my_creatures')
    .update({ current_happiness: happiness })
    .eq('id', playerCreatureId);

  if (error) throw error;
}

/**
 * Set exploration companion
 */
export async function setExplorationCompanion(
  gameId: string,
  playerCreatureId: string | null
): Promise<void> {
  // First, unset all companions for this game
  const { error: unsetError } = await supabase
    .from('creature_catcher_my_creatures')
    .update({ is_exploring: false })
    .eq('game_id', gameId);

  if (unsetError) throw unsetError;

  // Then, if a creature was specified, set it as the companion
  if (playerCreatureId) {
    const { error: setError } = await supabase
      .from('creature_catcher_my_creatures')
      .update({ is_exploring: true })
      .eq('id', playerCreatureId);

    if (setError) throw setError;
  }
}

/**
 * Release a creature (return to wild)
 */
export async function releaseCreature(playerCreatureId: string): Promise<void> {
  logger.log('Releasing creature:', playerCreatureId);

  try {
    const { data: existingCreature, error: checkError } = await supabase
      .from('creature_catcher_my_creatures')
      .select('id, creature_id')
      .eq('id', playerCreatureId)
      .single();

    if (checkError) {
      logger.error('Creature not found:', checkError);
      throw new Error('Creature not found');
    }

    logger.log('Found creature to release:', existingCreature);

    const { data, error, count } = await supabase
      .from('creature_catcher_my_creatures')
      .delete()
      .eq('id', playerCreatureId)
      .select();

    if (error) {
      logger.error('Failed to release creature:', error);
      throw error;
    }

    logger.log('Delete result:', { data, count });

    const { data: stillExists, error: verifyError } = await supabase
      .from('creature_catcher_my_creatures')
      .select('id')
      .eq('id', playerCreatureId)
      .maybeSingle();

    if (stillExists) {
      logger.error('Delete appeared successful but creature still exists!');
      logger.error('This is likely an RLS policy issue.');
      throw new Error('Delete failed - creature still exists (RLS policy issue?)');
    }

    logger.log('Creature released and verified deleted');
  } catch (error) {
    logger.error('Error releasing creature:', error);
    throw error;
  }
}

/**
 * Unlock a territory
 */
export async function unlockTerritory(gameId: string, territoryId: number): Promise<void> {
  const { data, error } = await supabase
    .from('creature_catcher_games')
    .update({ unlocked_territories: territoryId })
    .eq('id', gameId)
    .select();

  if (error) throw error;
}

/**
 * Unlock a habitat
 */
export async function unlockHabitat(gameId: string, habitatId: number): Promise<void> {
  const { data, error } = await supabase
    .from('creature_catcher_games')
    .update({ unlocked_habitats: habitatId })
    .eq('id', gameId)
    .select();

  if (error) throw error;
}

/**
 * Delete a game (for reset)
 */
export async function deleteGame(gameId: string): Promise<void> {
  logger.log('Deleting game:', gameId);

  try {
    logger.log('Resetting unlock progress...');
    const { error: resetError } = await supabase
      .from('creature_catcher_games')
      .update({
        unlocked_habitats: 0,
        unlocked_territories: 0
      })
      .eq('id', gameId);

    if (resetError) {
      logger.error('Failed to reset unlock progress:', resetError);
    } else {
      logger.log('Unlock progress reset');
    }

    const tables = [
      'creature_catcher_my_creatures',
      'creature_catcher_field_journal',
      'creature_catcher_games'
    ];

    for (const table of tables) {
      const { error } = await supabase
        .from(table as any)
        .delete()
        .eq(table === 'creature_catcher_games' ? 'id' : 'game_id', gameId);

      if (error) {
        logger.error(`Failed to delete from ${table}:`, error);
        throw error;
      }
      logger.log(`Deleted from ${table}`);
    }

    logger.log('Game completely deleted');
  } catch (error) {
    logger.error('Error deleting game:', error);
    throw error;
  }
}